import type { ComputedRef, InjectionKey } from 'vue';
import type { ResolvedControlWidth } from './context';
import { computed, inject, provide, watchEffect } from 'vue';

/** What a field hands the control inside it, so the control needs no slot props to be wired up. */
export interface FieldControlState {
    id?: string
    describedBy?: string
    invalid: boolean
    disabled: boolean
    required: boolean
}

export interface FieldControlContext {
    state: ComputedRef<FieldControlState>
    /** A control reports its resolved width, so a row can give a filling control the free space. */
    reportWidth: (width: ResolvedControlWidth) => void
}

const fieldControlKey: InjectionKey<FieldControlContext | null> = Symbol('sphFieldControl');

export function provideFieldControl(context: FieldControlContext | null): void {
    provide(fieldControlKey, context);
}

export function injectFieldControl(): FieldControlContext | null {
    return inject(fieldControlKey, null);
}

interface OwnState {
    id?: string
    invalid?: boolean
    disabled?: boolean
    required?: boolean
}

/**
 * The state a control renders with. Its own `id` wins over the field's; the flags are or-ed, so a
 * control cannot re-enable itself inside a disabled field or fieldset.
 */
export function useFieldControl(own: () => OwnState, width?: () => ResolvedControlWidth): ComputedRef<FieldControlState> {
    const field = injectFieldControl();

    if (field && width) {
        watchEffect(() => field.reportWidth(width()));
    }

    return computed(() => {
        const state = own();
        const inherited = field?.state.value;

        return {
            id: state.id || inherited?.id,
            describedBy: inherited?.describedBy,
            invalid: !!(state.invalid || inherited?.invalid),
            disabled: !!(state.disabled || inherited?.disabled),
            required: !!(state.required || inherited?.required),
        };
    });
}
